// Simple logging utility for BoltOS
// Helps track what's working and what's breaking across 7 clinics
import { captureException as sentryCaptureException } from './sentry';

export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: any;
  userId?: string;
  locationId?: string;
  module?: string;
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 1000; // Keep last 1000 logs in memory

  private createLogEntry(
    level: LogLevel,
    message: string,
    data?: any,
    module?: string
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
      module,
      // You can add user/location context here later
    };
  }

  debug(message: string, data?: any, module?: string) {
    const entry = this.createLogEntry(LogLevel.DEBUG, message, data, module);
    this.addLog(entry);
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[${entry.timestamp}] [${module || 'APP'}] ${message}`, data || '');
    }
  }

  info(message: string, data?: any, module?: string) {
    const entry = this.createLogEntry(LogLevel.INFO, message, data, module);
    this.addLog(entry);
    console.info(`[${entry.timestamp}] [${module || 'APP'}] ${message}`, data || '');
  }

  warn(message: string, data?: any, module?: string) {
    const entry = this.createLogEntry(LogLevel.WARN, message, data, module);
    this.addLog(entry);
    console.warn(`[${entry.timestamp}] [${module || 'APP'}] ${message}`, data || '');
  }

  error(message: string, error?: any, module?: string) {
    const entry = this.createLogEntry(LogLevel.ERROR, message, error, module);
    this.addLog(entry);
    console.error(`[${entry.timestamp}] [${module || 'APP'}] ${message}`, error || '');

    // Send to Sentry in production
    if (import.meta.env.PROD) {
      if (error instanceof Error) {
        sentryCaptureException(error, { module, message });
      } else {
        sentryCaptureException(new Error(message), { data: error, module });
      }
    }

    // Also send to backend monitoring API
    if (import.meta.env.PROD) {
      this.sendToMonitoring(entry);
    }
  }

  private addLog(entry: LogEntry) {
    this.logs.push(entry);
    
    // Keep only the most recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
  }

  private sendToMonitoring(entry: LogEntry) {
    // Placeholder for sending to external monitoring service
    // Could be Sentry, LogRocket, or your own service
    try {
      fetch('/api/logs', {
        method: 'POST',
        redirect: 'error',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry),
      }).catch(() => {
        // Fail silently to avoid infinite loops
      });
    } catch {
      // Fail silently
    }
  }

  // Get recent logs for debugging
  getRecentLogs(count = 50): LogEntry[] {
    return this.logs.slice(-count);
  }

  // Get logs by level
  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logs.filter(log => log.level === level);
  }

  // Clear logs (useful for testing)
  clearLogs() {
    this.logs = [];
  }

  // Export logs for support/debugging
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }
}

// Create singleton instance
export const logger = new Logger();

// Convenience functions for common use cases
export const logUserAction = (action: string, data?: any) => {
  logger.info(`User action: ${action}`, data, 'USER');
};

export const logApiCall = (endpoint: string, method: string, success: boolean, data?: any) => {
  if (success) {
    logger.info(`API ${method} ${endpoint} succeeded`, data, 'API');
  } else {
    logger.error(`API ${method} ${endpoint} failed`, data, 'API');
  }
};

export const logPageView = (page: string, userId?: string) => {
  logger.info(`Page viewed: ${page}`, { userId }, 'NAVIGATION');
};

export const logError = (error: Error, context?: string) => {
  logger.error(`Error in ${context || 'unknown context'}`, {
    message: error.message,
    stack: error.stack,
  }, 'ERROR');
};
